<script setup>
import { computed, useSlots } from 'vue'
import BaseChatMessage from '../BaseChatMessage.vue'
import RatioBar from '@/components/RatioBar.vue'
import AudioBar from './AudioBar.vue'
import LoadingAnimation from '../LoadingAnimation.vue'
import UnknownWordExplanation from './UnknownWordExplanation.vue'

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  markedContent: {
    type: String,
    default: '',
  },
  stats: {
    type: Object,
    default: null,
  },
  unknownWordEntries: {
    type: Array,
    default: () => [],
  },
  canAddKnownWords: {
    type: Boolean,
    default: false,
  },
  unknownWordError: {
    type: String,
    default: '',
  },
  audioSource: {
    type: [Object, String],
    default: null,
  },
  audioUrl: {
    type: String,
    default: '',
  },
  audioError: {
    type: String,
    default: '',
  },
  isAudioLoading: {
    type: Boolean,
    default: false,
  },
  audioLabel: {
    type: String,
    default: 'AI response audio',
  },
  isResponseLoading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'request-add-known-word',
  'confirm-add-known-word',
  'cancel-add-known-word',
])

const slots = useSlots()

const renderedContent = computed(() => props.markedContent || props.content)
const unknownPercent = computed(() => Number(props.stats?.percentUnknown))
const hasStats = computed(
  () => props.stats?.percentUnknown !== undefined && Number.isFinite(unknownPercent.value),
)
const hasAudioSlot = computed(() => Boolean(slots.audio))
const audioBarProps = computed(() => ({
  audioSource: props.audioSource,
  audioUrl: props.audioUrl,
  audioError: props.audioError,
  isAudioLoading: props.isAudioLoading,
  audioLabel: props.audioLabel,
}))
</script>

<template>
  <BaseChatMessage variant="ai">
    <div class="message-context">
      <div class="message-content">
        <span v-if="renderedContent" v-html="renderedContent" />
        <LoadingAnimation
          v-if="isResponseLoading"
          class="typing-indicator"
          label="Response is still loading"
        />
      </div>
    </div>

    <AudioBar v-bind="audioBarProps">
      <template v-if="hasAudioSlot" #default="audioSlotProps">
        <slot name="audio" v-bind="audioSlotProps" />
      </template>
    </AudioBar>

    <RatioBar v-if="hasStats" :unknown="unknownPercent" />

    <UnknownWordExplanation
      v-for="entry in unknownWordEntries"
      :key="entry.word"
      :word="entry.word"
      :explanation="entry.explanation"
      :explanation-error="entry.explanationError"
      :is-explanation-loading="entry.isExplanationLoading"
      :is-confirming="entry.isConfirming"
      :is-saving="entry.isSaving"
      :is-saved="entry.isSaved"
      :can-add-known-words="canAddKnownWords"
      @request-add-known-word="emit('request-add-known-word', $event)"
      @confirm-add-known-word="emit('confirm-add-known-word', $event)"
      @cancel-add-known-word="emit('cancel-add-known-word', $event)"
    />
    <p v-if="unknownWordError" class="unknown-word-error">
      {{ unknownWordError }}
    </p>
  </BaseChatMessage>
</template>

<style scoped>
.message-context {
  position: sticky;
  top: 0.75rem;
  z-index: 1;
  margin: -0.2rem -0.2rem 0.5rem;
  padding: 0.5rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.94));
  box-shadow: 0 6px 12px rgba(15, 23, 42, 0.08);
  border-radius: 6px;
}

.message-content {
  white-space: pre-wrap;
}

.typing-indicator {
  margin-left: 2px;
}

:deep(.focus-word-highlight) {
  color: #8a4b00;
  background: rgba(255, 196, 77, 0.28);
  border-radius: 4px;
  padding: 0 2px;
  font-weight: 600;
}

.unknown-word-error {
  margin: 0.5rem 0 0;
  color: #8b1e1e;
  font-weight: 700;
}
</style>
